import { transition } from '@studiometa/js-toolkit/utils';

const DURATION = '0.8s';
const EASING = 'cubic-bezier(0.65, 0, 0.35, 1)';

// clip-path states (inset: top right bottom left).
const HIDDEN_BOTTOM = 'inset(100% 0 0 0)'; // collapsed at the bottom (reveal seam down)
const VISIBLE = 'inset(0 0 0 0)'; // fully shown
const HIDDEN_TOP = 'inset(0 0 100% 0)'; // collapsed at the top (exit seam up)

/**
 * Hover wipe animation for a stack of identical thumbnail layers.
 *
 * On hover the thumbnail wipes up from the bottom; on leave it wipes out through
 * the top. Reveals alternate between layers so a still-hiding layer keeps playing
 * while the next wipes in — letting both animations overlap. Per layer the wipes
 * are queued, so each one always plays to completion (a quick in/out reveals
 * entirely, then hides).
 */
export default class ThumbnailWipe {
  /**
   * @param {HTMLElement|HTMLElement[]} [layers] The stacked thumbnail layers (expects two).
   */
  constructor(layers) {
    this.layers = Array.isArray(layers) ? layers : [layers].filter(Boolean);
    this.activeIndex = 0;
    // One queue per layer.
    this.queues = this.layers.map(() => Promise.resolve());
  }

  /**
   * Queue a wipe on a given layer so it starts only once that layer's previous
   * wipe has fully played out.
   * @param {number} index The layer index.
   * @param {() => Promise<unknown>} run The wipe to enqueue.
   * @returns {Promise<unknown>}
   */
  enqueue(index, run) {
    this.queues[index] = this.queues[index].then(run, run);
    return this.queues[index];
  }

  /**
   * Reveal a fresh layer with an upward wipe (from the bottom). Picks the layer
   * that isn't currently revealed so a still-hiding one keeps playing underneath.
   * @returns {Promise<unknown>}
   */
  reveal() {
    this.activeIndex = this.activeIndex === 0 ? 1 : 0;
    const index = this.activeIndex;

    return this.enqueue(index, () => {
      const layer = this.layers[index];
      if (!layer) {
        return Promise.resolve();
      }

      // Reset to the bottom instantly (no transition) so the wipe always starts
      // from the bottom; `transition: 'keep'` leaves the property inline, hence
      // the explicit reset + reflow rather than the util's `from` key.
      layer.style.transition = 'none';
      layer.style.clipPath = HIDDEN_BOTTOM;
      layer.getBoundingClientRect(); // force reflow to commit the start position

      return transition(
        layer,
        {
          active: { transition: `clip-path ${DURATION} ${EASING}` },
          to: { clipPath: VISIBLE },
        },
        'keep',
      );
    });
  }

  /**
   * Wipe the currently revealed layer out through the top, then reset it below.
   * @returns {Promise<unknown>}
   */
  hide() {
    const index = this.activeIndex;

    return this.enqueue(index, async () => {
      const layer = this.layers[index];
      if (!layer) {
        return;
      }

      await transition(
        layer,
        {
          active: { transition: `clip-path ${DURATION} ${EASING}` },
          to: { clipPath: HIDDEN_TOP },
        },
        'keep',
      );

      // Instant reset (no transition) so the seam jumps back to the bottom
      // without flashing a middle band of the image.
      layer.style.transition = 'none';
      layer.style.clipPath = HIDDEN_BOTTOM;
    });
  }
}
